import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Navigation from '../components/Navigation'
import { apiClient, type UpdateProfileRequest } from '../services/apiClient'
import { COUNTRIES } from '../constants/countries'

export default function ProfileEditPage() {
  const { user, refreshUser } = useAuth()
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState<UpdateProfileRequest>({
    firstName: '',
    lastName: '',
    displayName: '',
    country: '',
  })
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Initialize form with current user data
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        displayName: user.displayName || '',
        country: user.country || '',
      })
    }
  }, [user])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (error) setError(null)
    if (success) setSuccess(false)
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (error) setError(null)
    if (success) setSuccess(false)
  }

  // Common countries list (same as RegisterPage)
  const countries = COUNTRIES

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      // Only send fields that have changed
      const updates: UpdateProfileRequest = {}
      if (formData.firstName !== user?.firstName) updates.firstName = formData.firstName
      if (formData.lastName !== user?.lastName) updates.lastName = formData.lastName
      if (formData.displayName !== user?.displayName) updates.displayName = formData.displayName
      if (formData.country !== user?.country) updates.country = formData.country

      // If no changes, don't make API call
      if (Object.keys(updates).length === 0) {
        setSuccess(true)
        setTimeout(() => navigate('/dashboard'), 1500)
        return
      }

      await apiClient.updateUserProfile(updates)
      
      // Refresh user data in the auth context
      await refreshUser()
      
      setSuccess(true)
      
      // Redirect to dashboard after success
      setTimeout(() => navigate('/dashboard'), 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/dashboard')
  }

  // This shouldn't happen due to route protection, but adding safety check
  if (!user) {
    return (
      <div className="min-h-screen bg-firedup-dark flex items-center justify-center">
        <div className="text-firedup-orange text-xl">Redirecting...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="max-w-2xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-white mb-2">
            Edit <span className="text-firedup-orange">Profile</span>
          </h1>
          <p className="text-gray-300">
            Update your personal information
          </p>
        </div>

        <div className="bg-firedup-gray/50 backdrop-blur-sm p-8 rounded-lg border border-firedup-light-gray">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-900/30 border border-red-700 text-red-100 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-900/30 border border-green-700 text-green-100 px-4 py-3 rounded-lg">
                Profile updated successfully! Redirecting to dashboard...
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-firedup-light-gray border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-firedup-orange focus:border-transparent"
                  placeholder="Enter your first name"
                  required
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-firedup-light-gray border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-firedup-orange focus:border-transparent"
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-2">
                Display Name
              </label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-firedup-light-gray border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-firedup-orange focus:border-transparent"
                placeholder="Enter your display name"
              />
            </div>

            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-300 mb-2">
                Country
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleSelectChange}
                className="w-full px-4 py-3 bg-firedup-light-gray border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-firedup-orange focus:border-transparent"
                required
              >
                <option value="">Select your country</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            <div className="text-sm text-gray-400 bg-firedup-light-gray/30 p-4 rounded-lg">
              <p className="font-medium mb-2">📧 Email: {user.email}</p>
              <p>Email cannot be changed from this page. Contact support if you need to update your email address.</p>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
              
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
